import HttpService from './http-service';
import EdudoResponse from '../models/edudo-response';

export default class GalleryService {
  private schoolUuid: string;

  constructor(schoolUuid: string) {
    this.schoolUuid = schoolUuid;
  }

  private basePath(): string {
    return `/v1/schools/${this.schoolUuid}/galleries`;
  }

  // `school-backoffice-bff` is a top-level path prefix, not nested under `v1/schools/...` —
  // it's a distinct edge surface composing over (not replacing) the canonical routes above.
  private backofficeBasePath(): string {
    return `/school-backoffice-bff/v1/schools/${this.schoolUuid}/galleries`;
  }

  async listBackoffice(
    limit: number = 20,
    offset: number = 0
  ): Promise<BackofficeGalleryListItemDto[]> {
    const response = await HttpService.getJson<
      EdudoResponse<BackofficeGalleryListItemDto[]>
    >(`${this.backofficeBasePath()}?limit=${limit}&offset=${offset}`);

    return response.data;
  }

  // A tela de listagem não pagina, e a lista legada trazia todas as galerias:
  // percorre as páginas até a última (o BFF aceita no máximo 100 por página).
  async listAllBackoffice(): Promise<BackofficeGalleryListItemDto[]> {
    const pageSize = 100;
    const all: BackofficeGalleryListItemDto[] = [];

    for (let offset = 0; ; offset += pageSize) {
      const page = await this.listBackoffice(pageSize, offset);
      all.push(...page);
      if (page.length < pageSize) return all;
    }
  }

  async getBackoffice(galleryId: string): Promise<BackofficeGalleryDetailDto> {
    const response = await HttpService.getJson<EdudoResponse<BackofficeGalleryDetailDto>>(
      `${this.backofficeBasePath()}/${galleryId}`
    );

    return response.data;
  }

  async listGalleries(
    limit: number = 20,
    offset: number = 0
  ): Promise<GallerySummaryDto[]> {
    const response = await HttpService.getJson<EdudoResponse<GallerySummaryDto[]>>(
      `${this.basePath()}?limit=${limit}&offset=${offset}`
    );

    return response.data;
  }

  // Rota canônica: devolve os uuids crus (files/recipients) no formato que o PUT
  // espera de volta. É a que o formulário de edição usa, e não a do BFF.
  async getGallery(galleryId: string): Promise<GalleryDto> {
    const response = await HttpService.getJson<EdudoResponse<GalleryDto>>(
      `${this.basePath()}/${galleryId}`
    );

    return response.data;
  }

  async createGallery(data: GalleryRequest): Promise<GalleryDto> {
    const response = await HttpService.postJson<EdudoResponse<GalleryDto>>(
      this.basePath(),
      data
    );

    return response.data;
  }

  async updateGallery(galleryId: string, data: GalleryRequest): Promise<GalleryDto> {
    const response = await HttpService.putJson<EdudoResponse<GalleryDto>>(
      `${this.basePath()}/${galleryId}`,
      data
    );

    return response.data;
  }

  async deleteGallery(galleryId: string): Promise<void> {
    await HttpService.delete(`${this.basePath()}/${galleryId}`);
  }
}
